import React from 'react';
import { motion } from 'framer-motion';
import { 
  Sparkles, 
  PlayCircle, 
  Sun, 
  Heart, 
  Smile, 
  Star,
  ChevronRight,
  Waves,
  Music,
  Palette,
  BookOpen,
  Headphones
} from 'lucide-react';

const CHECKOUT_URL = "https://gillesdelieuze.com/joie-dauphin-checkout/";

// Animation variants
const fadeUpVariant = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
};

const fadeInVariant = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.8 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 }
  }
};

// Floating Bubbles Background
const FloatingBubbles = () => {
  const bubbles = Array.from({ length: 15 }, (_, i) => ({
    id: i,
    size: Math.random() * 60 + 20,
    left: Math.random() * 100,
    delay: Math.random() * 10,
    duration: Math.random() * 10 + 15
  }));

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {bubbles.map((bubble) => (
        <div
          key={bubble.id}
          className="bubble"
          style={{
            width: bubble.size,
            height: bubble.size,
            left: `${bubble.left}%`,
            animationDuration: `${bubble.duration}s`,
            animationDelay: `${bubble.delay}s`
          }}
        />
      ))}
    </div>
  );
};

// Hero Section
const HeroSection = () => (
  <section className="relative min-h-[95vh] flex items-center justify-center overflow-hidden bg-gradient-to-b from-cyan-50 via-teal-50 to-[#F0FDFA]" data-testid="hero-section">
    <FloatingBubbles />
    
    {/* Decorative light rays */}
    <div className="absolute top-0 left-1/4 w-96 h-96 bg-gradient-radial from-amber-100/40 to-transparent rounded-full blur-3xl" />
    <div className="absolute top-20 right-1/4 w-80 h-80 bg-gradient-radial from-teal-100/50 to-transparent rounded-full blur-3xl" />
    
    <div className="relative z-10 max-w-6xl mx-auto px-6 py-20 text-center">
      {/* Logo */}
      <motion.img 
        src="https://gillesdelieuze.com/wp-content/uploads/2022/12/Logo_Final_3bd-.png"
        alt="Gilles Delieuze Logo"
        className="h-16 md:h-20 mx-auto mb-12"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6 }}
        data-testid="logo"
      />

      <motion.div
        initial="hidden"
        animate="visible"
        variants={staggerContainer}
        className="space-y-8"
      >
        <motion.h1 
          variants={fadeUpVariant}
          className="font-heading text-5xl md:text-7xl lg:text-8xl font-semibold text-slate-800 leading-tight"
        >
          Renaître à la <span className="italic gradient-text">joie</span>
          <br />
          <span className="text-4xl md:text-5xl lg:text-6xl font-normal text-teal-700">
            grâce à l'énergie des dauphins
          </span>
        </motion.h1>

        <motion.p 
          variants={fadeUpVariant}
          className="text-xl md:text-2xl text-slate-600 max-w-3xl mx-auto font-light leading-relaxed"
        >
          Libère-toi du poids du passé et réactive ton feu intérieur grâce à un protocole simple et puissant
        </motion.p>

        <motion.div variants={fadeUpVariant}>
          <a 
            href={CHECKOUT_URL}
            className="cta-button inline-flex items-center gap-3 bg-gradient-to-r from-teal-400 to-cyan-500 text-white font-semibold px-10 py-5 rounded-full shadow-xl shadow-teal-200/50 hover:shadow-teal-300/60 transform hover:-translate-y-1 hover:scale-105 transition-all duration-300 text-lg"
            data-testid="hero-cta"
          >
            <Sparkles className="w-5 h-5" />
            OUI je choisis la Joie
            <ChevronRight className="w-5 h-5" />
          </a>
        </motion.div>
      </motion.div>

      {/* Floating dolphin image */}
      <motion.img
        src="https://gillesdelieuze.com/wp-content/uploads/2025/03/gilles1385_dolphins_joy_happiness_neon_stellar_furist_ultra_det_be59d961-0354-4dd1-b64e-6114a57150c8-300x300.png"
        alt="Joyful Dolphin"
        className="w-48 md:w-64 mx-auto mt-12 animate-float rounded-3xl"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 0.5 }}
        data-testid="hero-dolphin-image"
      />
    </div>

    {/* Wave divider */}
    <div className="absolute bottom-0 left-0 right-0">
      <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="#F0FDFA"/>
      </svg>
    </div>
  </section>
);

// Intro Section
const IntroSection = () => (
  <section className="py-24 md:py-32 bg-[#F0FDFA]" data-testid="intro-section">
    <div className="max-w-6xl mx-auto px-6">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={staggerContainer}
        className="grid md:grid-cols-2 gap-12 items-center"
      >
        <motion.div variants={fadeUpVariant} className="space-y-6">
          <h2 className="font-heading text-4xl md:text-5xl font-semibold text-slate-800">
            Renaître à la <span className="italic text-teal-600">Joie</span>
          </h2>
          
          <ul className="space-y-4">
            {[
              "Tu te sens parfois vidé(e), comme si ta joie était à l'arrêt…",
              "Tu aimerais retrouver cette légèreté enfantine, rire à nouveau pour rien, ressentir la vie comme une fête intérieure…",
              "Et si tu pouvais te reconnecter à la joie immuable que les dauphins incarnent depuis toujours ?"
            ].map((item, idx) => (
              <motion.li 
                key={idx}
                variants={fadeUpVariant}
                className="flex items-start gap-4 text-slate-600 text-lg"
              >
                <Waves className="w-6 h-6 text-teal-500 flex-shrink-0 mt-1" />
                <span>{item}</span>
              </motion.li>
            ))}
          </ul>

          <motion.p variants={fadeUpVariant} className="text-xl text-slate-700 font-medium pt-4">
            Le programme Quantique <strong className="text-teal-600">Renaître à la joie</strong> est exactement ce qu'il te faut pour te réaccorder à la fréquence de la joie, de la fluidité et du kif
          </motion.p>
        </motion.div>

        <motion.div 
          variants={fadeUpVariant}
          className="relative"
        >
          <div className="glass-card rounded-3xl p-8 text-center relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-amber-100/50 to-transparent rounded-bl-full" />
            
            <p className="text-slate-500 line-through text-lg mb-2">Au lieu de 333€</p>
            <p className="font-heading text-7xl font-bold gradient-gold-text mb-4">197€</p>
            
            <a 
              href={CHECKOUT_URL}
              className="cta-button inline-flex items-center gap-2 bg-gradient-to-r from-amber-400 to-amber-500 text-white font-semibold px-8 py-4 rounded-full shadow-lg shadow-amber-200/50 hover:shadow-amber-300/60 transform hover:-translate-y-1 transition-all duration-300"
              data-testid="intro-cta"
            >
              Je rejoins
              <ChevronRight className="w-5 h-5" />
            </a>

            <div className="flex items-center justify-center gap-1 mt-6">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
              ))}
              <span className="ml-2 text-slate-600 text-sm">Noté 4.7 sur 5 • 208 reviews</span>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  </section>
);

// Description Section
const DescriptionSection = () => (
  <section className="py-24 bg-gradient-to-b from-[#F0FDFA] to-[#ECFEFF]" data-testid="description-section">
    <div className="max-w-5xl mx-auto px-6">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={staggerContainer}
        className="grid md:grid-cols-2 gap-12 items-center"
      >
        <motion.img
          variants={fadeUpVariant}
          src="https://gillesdelieuze.com/wp-content/uploads/2024/11/gilles1385_photo_futuriste_figuratif_Dolphin_Celestial_violet_d_1528da77-503d-4b09-932a-c40dc1dccfaf.png"
          alt="Celestial Dolphin"
          className="rounded-[2.5rem] shadow-2xl animate-float-slow"
          data-testid="description-image"
        />
        
        <motion.div variants={fadeUpVariant} className="space-y-6">
          <h3 className="font-heading text-3xl md:text-4xl font-semibold text-slate-800">
            Renaître avec la joie grâce à l'énergie des dauphins
          </h3>
          
          <p className="text-slate-600 text-lg leading-relaxed">
            Un protocole simple, vibrant et profond à vivre depuis chez toi.
          </p>
          
          <p className="text-slate-600 text-lg leading-relaxed">
            Pendant des millénaires, les dauphins ont été perçus comme les gardiens de la joie originelle. Leur énergie turquoise et rose porte une fréquence rare : <strong className="text-teal-700">celle qui réveille ton enfant intérieur tout en apaisant ton système nerveux.</strong>
          </p>
          
          <p className="text-slate-600 text-lg leading-relaxed">
            Avec ce programme, tu ne vas pas seulement « méditer »…
          </p>
          
          <p className="text-xl font-semibold text-teal-700">
            Tu vas <em>activer</em> en toi la vibration de la joie stable, libre et expansive.
          </p>
        </motion.div>
      </motion.div>
    </div>
  </section>
);

// Benefits Section
const BenefitsSection = () => {
  const benefits = [
    { icon: Heart, text: "Te libérer des lourdeurs émotionnelles qui étouffent ton élan vital." },
    { icon: Smile, text: "Réactiver ton enfant libre : spontanéité, rire, curiosité, émerveillement." },
    { icon: Waves, text: "Apaiser ton système nerveux grâce aux fréquences douces et puissantes des dauphins." },
    { icon: Sparkles, text: "Ancrer un état de joie profonde qui ne dépend plus des circonstances." },
    { icon: Sun, text: "Retrouver l'énergie et l'enthousiasme nécessaires pour tes projets, tes relations, ta vie entière." }
  ];

  return (
    <section className="py-24 md:py-32 bg-[#ECFEFF]" data-testid="benefits-section">
      <div className="max-w-6xl mx-auto px-6">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={staggerContainer}
        >
          <motion.div variants={fadeUpVariant} className="text-center mb-16">
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-slate-800 mb-4">
              Les bénéfices <span className="italic text-teal-600">concrets</span>
            </h2>
            <p className="text-xl text-slate-600">
              En rejoignant <em>Renaître avec la joie</em> tu vas :
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, idx) => (
              <motion.div
                key={idx}
                variants={fadeUpVariant}
                className="benefit-card bg-white rounded-3xl p-8 shadow-sm hover:shadow-xl transition-all duration-500 border border-teal-50 group hover:-translate-y-2"
                data-testid={`benefit-card-${idx}`}
              >
                <div className="benefit-icon w-14 h-14 rounded-2xl bg-gradient-to-br from-teal-100 to-cyan-100 flex items-center justify-center mb-5 transition-transform duration-300">
                  <benefit.icon className="w-7 h-7 text-teal-600" />
                </div>
                <p className="text-slate-700 text-lg leading-relaxed">{benefit.text}</p>
              </motion.div>
            ))}
          </div>

          <motion.div variants={fadeUpVariant} className="mt-12 flex justify-center">
            <img
              src="https://gillesdelieuze.com/wp-content/uploads/2024/11/gilles1385_photo_futuriste_figuratif_Dolphin_Celestial_turquois_8cbec288-5125-4e13-8bab-c1d122dcb180.png"
              alt="Turquoise Dolphin"
              className="w-64 md:w-80 animate-float-delayed rounded-3xl"
              data-testid="benefits-image"
            />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// What You Receive Section
const ProgramContentsSection = () => {
  const contents = [
    { icon: PlayCircle, title: "Un soin vibratoire de 52 minutes", description: "Un bain d'énergie qui dissout blocages, tensions et fatigue émotionnelle." },
    { icon: PlayCircle, title: "Une deuxième vidéo-soin exclusive", description: "Pour ancrer la joie et libérer ton système nerveux." },
    { icon: Palette, title: "Des mandalas vibratoires sur la joie", description: "À imprimer, colorier, méditer… pour réactiver chaque jour ton rayonnement." },
    { icon: BookOpen, title: "Un protocole 'Joie des dauphins'", description: "Pratique simple et guidée pour entrer dans cette fréquence dès que tu en as besoin." },
    { icon: Music, title: "Une méditation guidée", description: "Pour renaître et stabiliser la joie comme état naturel." },
    { icon: Headphones, title: "Une méditation binaurale", description: "Pour activer les voies neurales de la joie." }
  ];

  return (
    <section className="py-24 md:py-32 bg-gradient-to-b from-[#ECFEFF] to-[#F0FDFA]" data-testid="contents-section">
      <div className="max-w-6xl mx-auto px-6">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={staggerContainer}
        >
          <motion.div variants={fadeUpVariant} className="text-center mb-16">
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-slate-800 mb-4">
              Ce que tu <span className="italic gradient-gold-text">reçois</span>
            </h2>
            <p className="text-xl text-slate-600">Tu accèdes immédiatement à :</p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {contents.map((item, idx) => (
              <motion.div
                key={idx}
                variants={fadeUpVariant}
                className="glass-card rounded-3xl p-8 hover:bg-white/80 transition-all duration-500 group"
                data-testid={`content-card-${idx}`}
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-100 to-amber-200 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                    <item.icon className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <h4 className="font-heading text-xl font-semibold text-slate-800 mb-2">{item.title}</h4>
                    <p className="text-slate-600">{item.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div variants={fadeUpVariant} className="mt-12 flex justify-center">
            <img
              src="https://gillesdelieuze.com/wp-content/uploads/2024/11/gilles1385_photo_futuriste_figuratif_Dolphin_Celestial_green_da_a98140e4-6ba3-4494-9961-363df18719aa.png"
              alt="Green Celestial Dolphin"
              className="w-64 md:w-80 animate-float rounded-3xl"
              data-testid="contents-image"
            />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Why Dolphins Section
const WhyDolphinsSection = () => (
  <section className="py-24 md:py-32 bg-gradient-to-br from-teal-700 via-teal-800 to-cyan-900 relative overflow-hidden" data-testid="why-dolphins-section">
    <FloatingBubbles />
    
    {/* Light rays */}
    <div className="absolute top-0 left-1/4 w-96 h-[600px] bg-gradient-to-b from-teal-400/10 to-transparent rotate-12 blur-2xl" />
    <div className="absolute top-20 right-1/3 w-64 h-[400px] bg-gradient-to-b from-cyan-400/10 to-transparent -rotate-12 blur-2xl" />
    
    <div className="max-w-5xl mx-auto px-6 relative z-10">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={staggerContainer}
        className="text-center text-white"
      >
        <motion.h2 
          variants={fadeUpVariant}
          className="font-heading text-4xl md:text-5xl lg:text-6xl font-semibold mb-8"
        >
          Pourquoi les <span className="italic text-amber-300">dauphins</span> ?
        </motion.h2>
        
        <motion.p 
          variants={fadeUpVariant}
          className="text-xl md:text-2xl text-teal-100 leading-relaxed max-w-3xl mx-auto mb-8"
        >
          Parce qu'ils sont l'incarnation vivante de la joie libre, sans masque ni condition.
        </motion.p>
        
        <motion.p 
          variants={fadeUpVariant}
          className="text-lg md:text-xl text-teal-200 leading-relaxed max-w-3xl mx-auto mb-8"
        >
          Leur vibration turquoise-rose réactive directement dans ton champ énergétique la <strong className="text-white">légèreté de l'être</strong> et l'<strong className="text-white">amour de la vie</strong>.
        </motion.p>
        
        <motion.p 
          variants={fadeUpVariant}
          className="text-lg md:text-xl text-amber-200 font-medium"
        >
          Ici, tu ne reçois pas un concept spirituel : tu vis une <strong className="text-amber-300">expérience énergétique directe.</strong>
        </motion.p>
      </motion.div>
    </div>
  </section>
);

// Pricing Section
const PricingSection = () => (
  <section className="py-24 md:py-32 bg-[#F0FDFA]" data-testid="pricing-section">
    <div className="max-w-4xl mx-auto px-6">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={fadeUpVariant}
        className="text-center"
      >
        <h2 className="font-heading text-4xl md:text-5xl font-semibold text-slate-800 mb-6">
          Offre <span className="italic gradient-gold-text">spéciale</span>
        </h2>
        
        <p className="text-xl text-slate-600 mb-12">
          Le prix réel de ce protocole est de <strong>333 €</strong>.
          <br />
          Mais aujourd'hui, tu peux y accéder pour seulement :
        </p>

        <motion.div 
          className="relative bg-white border-2 border-amber-200 rounded-[2.5rem] p-12 shadow-2xl max-w-lg mx-auto"
          whileHover={{ scale: 1.02 }}
          transition={{ duration: 0.3 }}
        >
          {/* Golden corner accent */}
          <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-bl from-amber-200 to-transparent rounded-bl-[3rem] rounded-tr-[2.5rem]" />
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-teal-100 to-transparent rounded-tr-[3rem] rounded-bl-[2.5rem]" />
          
          <p className="text-slate-500 line-through text-lg mb-2">333 €</p>
          <p className="font-heading text-8xl font-bold gradient-gold-text mb-2">197€</p>
          <p className="text-teal-600 font-medium mb-8">Offre à durée limitée</p>
          
          <a 
            href={CHECKOUT_URL}
            className="cta-button inline-flex items-center gap-3 bg-gradient-to-r from-amber-400 via-amber-500 to-amber-400 text-white font-bold px-12 py-5 rounded-full shadow-xl shadow-amber-200/50 hover:shadow-amber-300/60 transform hover:-translate-y-1 hover:scale-105 transition-all duration-300 text-lg uppercase tracking-wider animate-shimmer"
            style={{ backgroundSize: '200% auto' }}
            data-testid="pricing-cta"
          >
            <Sparkles className="w-5 h-5" />
            Je rejoins
            <ChevronRight className="w-5 h-5" />
          </a>
        </motion.div>
      </motion.div>
    </div>
  </section>
);

// Target Audience Section
const TargetAudienceSection = () => {
  const audiences = [
    "Pour celles et ceux qui se sentent fatigués, lourds, pris dans le mental.",
    "Pour les hypersensibles qui veulent stabiliser leur énergie.",
    "Pour les chercheurs de sens qui veulent goûter à la profondeur sans complexité.",
    "Pour celles et ceux qui veulent une expérience claire, simple et puissante de l'énergie des dauphins."
  ];

  return (
    <section className="py-24 md:py-32 bg-gradient-to-b from-[#F0FDFA] to-[#ECFEFF]" data-testid="audience-section">
      <div className="max-w-6xl mx-auto px-6">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={staggerContainer}
          className="grid md:grid-cols-2 gap-12 items-center"
        >
          <motion.div variants={fadeUpVariant}>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-slate-800 mb-8">
              Pour qui est-ce <span className="italic text-teal-600">fait</span> ?
            </h2>
            
            <ul className="space-y-4">
              {audiences.map((item, idx) => (
                <motion.li 
                  key={idx}
                  variants={fadeUpVariant}
                  className="flex items-start gap-4 text-slate-600 text-lg"
                >
                  <div className="w-6 h-6 rounded-full bg-gradient-to-br from-teal-400 to-cyan-400 flex items-center justify-center flex-shrink-0 mt-1">
                    <ChevronRight className="w-4 h-4 text-white" />
                  </div>
                  <span>{item}</span>
                </motion.li>
              ))}
            </ul>

            <motion.div variants={fadeUpVariant} className="mt-8 p-6 bg-white/60 backdrop-blur-sm rounded-2xl border border-teal-100">
              <p className="text-slate-700 text-lg">
                Ce protocole est fait pour être <strong className="text-teal-700">simple, actionnable et transformateur.</strong>
              </p>
              <p className="text-slate-600 mt-3">
                Tu n'as pas besoin d'y « croire ». Tu vas le ressentir, au fur et à mesure que tu pratiques avec conscience et assiduité.
              </p>
            </motion.div>
          </motion.div>

          <motion.div variants={fadeUpVariant} className="flex justify-center">
            <img
              src="https://gillesdelieuze.com/wp-content/uploads/2025/05/gilles1385_full_body_King_dolphin_anima_celestial_futurist_gala_c5b0e502-9780-406c-93f5-21287de4d5cd.PNG.png"
              alt="King Dolphin"
              className="w-80 md:w-96 animate-float-slow rounded-[2.5rem]"
              data-testid="audience-image"
            />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  const testimonials = [
    {
      name: "Claire",
      age: "42 ans",
      image: "https://gillesdelieuze.com/wp-content/uploads/2025/09/happy-dog-3-free-img-1-1-1.jpg",
      text: "J'étais persuadée que rien ne pouvait vraiment m'aider à retrouver ma joie. J'ai fait des stages, des thérapies, lu des dizaines de livres… et pourtant, j'avais toujours ce poids dans la poitrine. Quand j'ai vécu le soin avec les dauphins, j'ai senti quelque chose que je n'avais jamais ressenti : un relâchement profond, comme si une armure tombait. Aujourd'hui, je ris à nouveau sans raison, je me sens légère et vivante. Ce protocole est le premier qui a vraiment fait bouger quelque chose en profondeur."
    },
    {
      name: "Karim",
      age: "47 ans",
      image: "https://gillesdelieuze.com/wp-content/uploads/2025/09/gilles1385_Homme_40_ans_karim_qui_vit_en_suisse_petite_lunett_a883f5c4-1616-4f40-93ff-0a830eb25088_3.png",
      text: "Je n'ai pas beaucoup de temps avec mon boulot et mes enfants, et je pensais que ce genre de programme allait être trop compliqué pour moi. Mais là, c'était d'une simplicité incroyable : une vidéo, une méditation, un protocole que je peux refaire quand je veux… Et pourtant, les effets ont été énormes ! Dès la première pratique, j'ai senti mon corps se détendre, mes pensées s'apaiser, et surtout une joie monter sans que j'aie besoin de forcer. C'est simple, accessible et puissant."
    },
    {
      name: "Élodie",
      age: "29 ans",
      image: "https://gillesdelieuze.com/wp-content/uploads/2025/06/gilles1385_femme_summer_vibes_plaisir_plage_lecture_sourire_nou_366c2f50-c377-446b-b52b-bf270bd67e7d.PNG.png",
      text: "Pour être honnête, je suis quelqu'un de rationnel et je me méfie de tout ce qui semble 'ésotérique'. Mais j'avais perdu goût à la vie, alors j'ai tenté. Le plus fou, c'est que je n'ai pas eu besoin d'y croire pour que ça marche : j'ai senti mon stress se dissoudre, comme si mon système nerveux avait enfin trouvé un bouton 'reset'. Et surtout, mes proches m'ont dit qu'ils me trouvaient transformée, plus rayonnante, plus ouverte. Alors peu importe comment on l'explique, ça fonctionne vraiment."
    }
  ];

  return (
    <section className="py-24 md:py-32 bg-[#ECFEFF]" data-testid="testimonials-section">
      <div className="max-w-6xl mx-auto px-6">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={staggerContainer}
        >
          <motion.h2 
            variants={fadeUpVariant}
            className="font-heading text-4xl md:text-5xl font-semibold text-slate-800 text-center mb-16"
          >
            Ils ont vécu la <span className="italic text-teal-600">transformation</span>
          </motion.h2>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, idx) => (
              <motion.div
                key={idx}
                variants={fadeUpVariant}
                className="testimonial-card bg-white rounded-3xl p-8 shadow-lg transition-all duration-500 border border-teal-50"
                data-testid={`testimonial-${idx}`}
              >
                <div className="flex items-center gap-4 mb-6">
                  <img 
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-16 h-16 rounded-2xl object-cover border-2 border-teal-100"
                  />
                  <div>
                    <p className="font-heading text-xl font-semibold text-slate-800">{testimonial.name}</p>
                    <p className="text-slate-500">{testimonial.age}</p>
                  </div>
                </div>
                
                <p className="text-slate-600 leading-relaxed italic">"{testimonial.text}"</p>
                
                <div className="flex items-center gap-1 mt-6">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                  ))}
                  <span className="ml-2 text-slate-500 text-sm">5/5</span>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// About Author Section
const AboutSection = () => (
  <section className="py-24 md:py-32 bg-gradient-to-b from-[#ECFEFF] to-[#F0FDFA]" data-testid="about-section">
    <div className="max-w-6xl mx-auto px-6">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={staggerContainer}
        className="grid md:grid-cols-2 gap-12 items-center"
      >
        <motion.div variants={fadeUpVariant} className="flex justify-center">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-teal-200 to-amber-100 rounded-[2rem] rotate-3" />
            <img
              src="https://gillesdelieuze.com/wp-content/uploads/2023/08/IMG_9881_www.vincentphotographie.com_-683x1024.jpg"
              alt="Gilles Delieuze"
              className="relative w-72 md:w-80 rounded-[2rem] shadow-xl object-cover"
              data-testid="author-image"
            />
          </div>
        </motion.div>

        <motion.div variants={fadeUpVariant} className="space-y-6">
          <p className="text-teal-600 font-medium uppercase tracking-widest text-sm">Votre guide</p>
          <h2 className="font-heading text-4xl md:text-5xl font-semibold text-slate-800">
            Gilles Delieuze
          </h2>
          
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-teal-600" />
              </div>
              <div>
                <p className="font-semibold text-slate-800">Enseignant et conférencier</p>
                <p className="text-slate-600">26 ans à vos côtés à travers le Monde</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="font-semibold text-slate-800">Auteur et Artiste</p>
                <p className="text-slate-600">L'oracle des Maîtres Dauphins et ses fameuses vidéos vibratoires</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-cyan-100 flex items-center justify-center">
                <Waves className="w-5 h-5 text-cyan-600" />
              </div>
              <div>
                <p className="font-semibold text-slate-800">Organisateur de Voyages sacrés</p>
                <p className="text-slate-600">Nage avec les dauphins, les baleines...</p>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  </section>
);

// Final CTA Section
const FinalCTASection = () => (
  <section className="py-24 md:py-32 bg-gradient-to-br from-teal-50 via-cyan-50 to-amber-50 relative overflow-hidden" data-testid="final-cta-section">
    <FloatingBubbles />
    
    <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={staggerContainer}
      >
        <motion.img
          variants={fadeUpVariant}
          src="https://gillesdelieuze.com/wp-content/uploads/2025/02/gilles1385_an_anime_style_and_luminous_dolphin__vertical_de_fac_f7f0de29-573a-46fc-b5ae-16adc3cf2ba6.png"
          alt="Luminous Dolphin"
          className="w-48 md:w-64 mx-auto mb-8 animate-float rounded-3xl"
          data-testid="final-cta-image"
        />
        
        <motion.h2 
          variants={fadeUpVariant}
          className="font-heading text-4xl md:text-5xl font-semibold text-slate-800 mb-4"
        >
          Plus de <span className="text-teal-600">Joie</span> • Plus de <span className="text-amber-500">Vie</span> • Plus de <span className="text-cyan-600">Conscience</span>
        </motion.h2>
        
        <motion.p 
          variants={fadeUpVariant}
          className="text-xl text-slate-600 mb-8"
        >
          Rejoins <em>Renaître avec la joie grâce à l'énergie des dauphins</em> et laisse la joie redevenir ton état naturel.
        </motion.p>

        <motion.div 
          variants={fadeUpVariant}
          className="inline-block bg-white/60 backdrop-blur-sm rounded-2xl p-8 border border-amber-100"
        >
          <p className="text-slate-600 mb-2">Seulement</p>
          <p className="font-heading text-6xl font-bold gradient-gold-text mb-4">197€</p>
          <p className="text-slate-500 line-through text-lg mb-6">au lieu de 333€</p>
          
          <a 
            href={CHECKOUT_URL}
            className="cta-button inline-flex items-center gap-3 bg-gradient-to-r from-teal-400 to-cyan-500 text-white font-bold px-12 py-5 rounded-full shadow-xl shadow-teal-200/50 hover:shadow-teal-300/60 transform hover:-translate-y-1 hover:scale-105 transition-all duration-300 text-lg"
            data-testid="final-cta-button"
          >
            <Sparkles className="w-5 h-5" />
            Je plonge
            <ChevronRight className="w-5 h-5" />
          </a>
        </motion.div>
      </motion.div>
    </div>

    {/* Bottom decoration */}
    <motion.img
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true }}
      src="https://gillesdelieuze.com/wp-content/uploads/2024/12/gilles1385_futurist_celestial_dolphin_divine_ascension_light_go_72297ae4-7b01-4ea0-9d50-6a02d9db123e.png"
      alt="Divine Dolphin"
      className="w-80 md:w-96 mx-auto mt-16 animate-float-slow rounded-[2.5rem]"
      data-testid="final-bottom-image"
    />
  </section>
);

// Main App Component
const JoieDauphinPage = () => {
  return (
    <div className="min-h-screen" data-testid="joie-dauphin-page">
      <HeroSection />
      <IntroSection />
      <DescriptionSection />
      <BenefitsSection />
      <ProgramContentsSection />
      <WhyDolphinsSection />
      <PricingSection />
      <TargetAudienceSection />
      <TestimonialsSection />
      <AboutSection />
      <FinalCTASection />
    </div>
  );
};

export default JoieDauphinPage;
